import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { MapPin, Calendar, ExternalLink, Map, List } from "lucide-react";
import { GoogleMap, LoadScript, Marker, InfoWindow } from "@react-google-maps/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const darkMapStyle = [
  { elementType: "geometry", stylers: [{ color: "#0A0A0A" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#0A0A0A" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#1a1a1a" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#212a37" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#050505" }] },
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#0d0d0d" }] },
  { featureType: "transit", elementType: "geometry", stylers: [{ color: "#151515" }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#FF005A", weight: 0.5 }] },
  { featureType: "administrative.country", elementType: "labels.text.fill", stylers: [{ color: "#9ca5b3" }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#c4c4c4" }] },
];

export default function KoncertyPage() {
  const { t, lang } = useLanguage();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    axios.get(`${API}/events`)
      .then(res => setEvents(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString(lang === "cz" ? "cs-CZ" : "en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  };

  const eventsWithCoords = events.filter(e => e.lat && e.lng);
  const mapCenter = eventsWithCoords.length > 0
    ? { lat: eventsWithCoords.reduce((s, e) => s + e.lat, 0) / eventsWithCoords.length, lng: eventsWithCoords.reduce((s, e) => s + e.lng, 0) / eventsWithCoords.length }
    : { lat: 49.8, lng: 15.5 };

  return (
    <div style={{ background: "#050505", minHeight: "100vh" }}>
      <SEO
        title="Koncerty 2026 – Bezvafest tour"
        description="Kompletní seznam nadcházejících koncertů kapely Rebelky pro rok 2026. Bezvafest tour po celé ČR – Praha, Brno, Hradec Králové, Plzeň, Ostrava. Termíny, místa a vstupenky online."
        keywords="Rebelky koncerty, Bezvafest 2026, koncert Praha, koncert Brno, kapela Rebelky vstupenky, dívčí kapela koncerty, festival 2026"
        url="/koncerty"
      />
      <Navbar />
      <div style={{ paddingTop: "5rem" }}>
        {/* Header */}
        <section style={{ padding: "4rem 1.5rem 2rem", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <div className="max-w-7xl mx-auto">
            <p className="section-label mb-3">{t.concerts.subtitle}</p>
            <div className="flex items-end justify-between flex-wrap gap-4">
              <h1 style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 900, fontSize: "clamp(2rem, 6vw, 4rem)", color: "#FFFFFF", textTransform: "uppercase", letterSpacing: "-0.03em" }}>
                {t.concerts.title}
              </h1>
              {/* View toggle */}
              <div className="flex gap-2" data-testid="view-toggle">
                <button
                  data-testid="list-view-btn"
                  onClick={() => setView("list")}
                  className="flex items-center gap-2"
                  style={{ background: view === "list" ? "#FF005A" : "transparent", border: "1px solid rgba(255,255,255,0.1)", color: "white", padding: "0.5rem 1rem", fontFamily: "Unbounded, sans-serif", fontSize: "0.6rem", letterSpacing: "0.15em", cursor: "pointer", borderRadius: "4px" }}
                >
                  <List size={14} /> {t.concerts.list}
                </button>
                <button
                  data-testid="map-view-btn"
                  onClick={() => setView("map")}
                  className="flex items-center gap-2"
                  style={{ background: view === "map" ? "#FF005A" : "transparent", border: "1px solid rgba(255,255,255,0.1)", color: "white", padding: "0.5rem 1rem", fontFamily: "Unbounded, sans-serif", fontSize: "0.6rem", letterSpacing: "0.15em", cursor: "pointer", borderRadius: "4px" }}
                >
                  <Map size={14} /> {t.concerts.map}
                </button>
              </div>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-6 py-8">
          {loading ? (
            <p style={{ color: "#52525B", fontFamily: "Unbounded, sans-serif", fontSize: "0.7rem", letterSpacing: "0.2em" }}>
              {t.common.loading}
            </p>
          ) : (
            <>
              {/* Map View */}
              {view === "map" && (
                <div data-testid="concerts-map" className="mb-8" style={{ borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <LoadScript googleMapsApiKey={MAPS_KEY}>
                    <GoogleMap
                      mapContainerStyle={{ width: "100%", height: "500px" }}
                      center={mapCenter}
                      zoom={7}
                      options={{ styles: darkMapStyle, disableDefaultUI: false, zoomControl: true }}
                    >
                      {eventsWithCoords.map(event => (
                        <Marker
                          key={event._id}
                          position={{ lat: event.lat, lng: event.lng }}
                          onClick={() => setSelectedEvent(event)}
                          icon={{
                            path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
                            fillColor: "#FF005A",
                            fillOpacity: 1,
                            strokeColor: "#FFFFFF",
                            strokeWeight: 1,
                            scale: 1.5,
                          }}
                        />
                      ))}
                      {selectedEvent && (
                        <InfoWindow
                          position={{ lat: selectedEvent.lat, lng: selectedEvent.lng }}
                          onCloseClick={() => setSelectedEvent(null)}
                        >
                          <div style={{ background: "#0A0A0A", padding: "0.75rem", minWidth: "200px" }}>
                            <p style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 700, fontSize: "0.8rem", color: "#FFFFFF", textTransform: "uppercase", marginBottom: "0.3rem" }}>
                              {selectedEvent.title}
                            </p>
                            <p style={{ color: "#A1A1AA", fontSize: "0.75rem" }}>{formatDate(selectedEvent.date)}</p>
                            <p style={{ color: "#A1A1AA", fontSize: "0.75rem" }}>{selectedEvent.location}</p>
                            {selectedEvent.time && <p style={{ color: "#A1A1AA", fontSize: "0.75rem" }}>{selectedEvent.time}</p>}
                            {selectedEvent.ticket_url && (
                              <a href={selectedEvent.ticket_url} target="_blank" rel="noopener noreferrer" style={{ color: "#FF005A", fontSize: "0.7rem", fontFamily: "Unbounded, sans-serif", marginTop: "0.5rem", display: "block" }}>
                                {t.concerts.buyTickets} →
                              </a>
                            )}
                          </div>
                        </InfoWindow>
                      )}
                    </GoogleMap>
                  </LoadScript>
                </div>
              )}

              {/* List View */}
              {events.length === 0 ? (
                <p style={{ color: "#52525B" }}>{t.concerts.noEvents}</p>
              ) : (
                <div className="flex flex-col gap-3">
                  {events.map((event, i) => (
                    <motion.div
                      key={event._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                      data-testid={`concert-item-${i}`}
                      style={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "12px", padding: "1.5rem", transition: "border-color 0.2s ease" }}
                      className="hover:border-pink-700"
                    >
                      <div className="flex items-start justify-between flex-wrap gap-4">
                        <div className="flex items-start gap-6">
                          {/* Date block */}
                          <div style={{ textAlign: "center", minWidth: "70px" }}>
                            <p style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 900, fontSize: "2.5rem", color: "#FF005A", lineHeight: 1 }}>
                              {new Date(event.date).getDate().toString().padStart(2, "0")}
                            </p>
                            <p style={{ fontFamily: "Unbounded, sans-serif", fontSize: "0.6rem", color: "#A1A1AA", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                              {new Date(event.date).toLocaleDateString(lang === "cz" ? "cs-CZ" : "en-GB", { month: "short", year: "numeric" })}
                            </p>
                          </div>

                          {/* Info */}
                          <div>
                            <h3 style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 700, fontSize: "1.1rem", color: "#FFFFFF", textTransform: "uppercase", marginBottom: "0.5rem" }}>
                              {event.title}
                            </h3>
                            <div className="flex items-center gap-4 flex-wrap">
                              <div className="flex items-center gap-1">
                                <MapPin size={13} style={{ color: "#FF005A" }} />
                                <span style={{ color: "#A1A1AA", fontSize: "0.85rem" }}>{event.location}</span>
                                {event.venue && <span style={{ color: "#52525B", fontSize: "0.85rem" }}>· {event.venue}</span>}
                              </div>
                              {event.time && (
                                <div className="flex items-center gap-1">
                                  <Calendar size={13} style={{ color: "#A1A1AA" }} />
                                  <span style={{ color: "#A1A1AA", fontSize: "0.85rem" }}>{event.time}</span>
                                </div>
                              )}
                            </div>
                            {event.ticket_price && (
                              <p style={{ color: "#52525B", fontSize: "0.8rem", marginTop: "0.4rem" }}>{event.ticket_price}</p>
                            )}
                            {event.description && (
                              <p style={{ color: "#A1A1AA", fontSize: "0.85rem", marginTop: "0.6rem", maxWidth: "500px" }}>{event.description}</p>
                            )}
                          </div>
                        </div>

                        {/* CTA */}
                        <div className="flex gap-2 items-center">
                          {event.lat && event.lng && (
                            <a
                              href={`https://maps.google.com/?q=${event.lat},${event.lng}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`map-link-${i}`}
                              className="btn-outline-rebel rounded-none flex items-center gap-1"
                              style={{ fontSize: "0.6rem", padding: "0.5rem 0.8rem" }}
                            >
                              <Map size={11} /> Google Maps
                            </a>
                          )}
                          {event.ticket_url ? (
                            <a
                              href={event.ticket_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`ticket-btn-${i}`}
                              className="btn-rebel rounded-none flex items-center gap-1"
                              style={{ fontSize: "0.6rem", padding: "0.5rem 1rem" }}
                            >
                              <ExternalLink size={11} /> {t.concerts.buyTickets}
                            </a>
                          ) : (
                            <span style={{ fontFamily: "Unbounded, sans-serif", fontSize: "0.6rem", color: "#00E5FF", border: "1px solid rgba(0,229,255,0.3)", padding: "0.5rem 1rem" }}>
                              {t.concerts.freeEntry}
                            </span>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
